import Image from 'next/image';
import React, { useEffect, useState } from 'react';

import { SITE_URL, STORYBLOK_IMAGE_QUALITY } from '@/lib/constants';

import styles from './HeroImage.module.scss';

type HeroImageProps = {
  eager?: boolean;
  blok: {
    image: {
      filename: string;
      alt: string;
    };
    layout: 'width' | 'height' | 'fill';
    height: string;
    width: string;
    mobileHeight?: string;
    mobileWidth?: string;
    mobileLayout?: string;
    fitToContent?: boolean;
  };
};

const HeroImage: React.FC<HeroImageProps> = ({ blok, eager }) => {
  const { image, height, width, layout, mobileWidth, mobileHeight, mobileLayout, fitToContent } =
    blok;
  const [filePath, setFilePath] = useState(null);
  const [mobileOptimisation, setMobileOptimisation] = useState(null);
  const imageIsSVG = image.filename.endsWith('.svg');

  const hasMobileLayout = !!(mobileLayout && (mobileWidth || mobileHeight));

  const inDev = process.env.NODE_ENV === 'development';
  const host = `${SITE_URL}cdn/`;

  const desktopImagePath = imageIsSVG
    ? image.filename
    : image.filename + `/m/2900x0/filters:quality(${STORYBLOK_IMAGE_QUALITY})`;

  // Fix image src hydration issue
  useEffect(() => {
    if (!image.filename) return;

    let filePath = image.filename;

    if (!inDev) {
      filePath = image.filename.replace('https://a.storyblok.com/', host);
    }

    setFilePath(filePath);
    setMobileOptimisation(
      imageIsSVG ? filePath : filePath + `/m/767x0/filters:quality(${STORYBLOK_IMAGE_QUALITY})`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);

  return (
    <div
      style={
        {
          '--img-width': width,
          '--img-height': height
        } as React.CSSProperties
      }
      className={[styles.container, fitToContent && styles.fitToContent].join(' ')}
    >
      {hasMobileLayout ? (
        <>
          <div className={[styles.image, styles.desktop, styles[layout]].join(' ')}>
            <img
              src={filePath}
              alt={image.alt}
              loading={eager ? 'eager' : 'lazy'}
              /*
              // @ts-ignore */
              fetchPriority={eager ? 'high' : 'low'}
            />
          </div>
          <div
            style={
              { '--img-width': mobileWidth, '--img-height': mobileHeight } as React.CSSProperties
            }
            className={[styles.image, styles.mobile, styles[mobileLayout]].join(' ')}
          >
            <img
              src={mobileOptimisation}
              alt={image.alt}
              loading={eager ? 'eager' : 'lazy'}
              /*
              // @ts-ignore */
              fetchPriority={eager ? 'high' : 'low'}
            />
          </div>
        </>
      ) : (
        <div className={[styles.image, styles[layout]].join(' ')}>
          <img
            src={desktopImagePath}
            alt={image.alt}
            loading={eager ? 'eager' : 'lazy'}
            /*
            // @ts-ignore */
            fetchPriority={eager ? 'high' : 'low'}
          />
        </div>
      )}
    </div>
  );
};

export default HeroImage;
